import React, { FC, useEffect } from 'react';
import { Switch, RouteComponentProps, Route, matchPath } from 'react-router';
import { UsersHome } from '../../Users';
import {
  CreatePartnerDetails,
  PartnersHome,
  PartnerType,
} from '../../Partners';
import {
  SecondaryMenu,
  SecondaryMenuLink,
} from '../../../Shared/components/SecondaryMenu';
import { AnimatePresence } from 'framer-motion';
import urlJoin from 'url-join';
import styles from './styles.module.css';
import { PartnerMenu } from '../PartnerMenu';
import { PartnerRouter } from '../../Partners/components/PartnerRouter';
import { useQuery } from '@apollo/react-hooks';
import { isMissing, isPresent } from '../../../Shared/utils/object';
import { PartnerContext } from '../../utils';

import { useAuth0 } from '../../../Auth';

import { AnimatedRedirect } from '../../../Shared/components/AnimatedScreen';
import { GrantsType } from 'Shared/utils';
import { createPartnerPolicy } from 'Admin/authPolicies';
import { NetworkError } from 'Admin/components/NetworkError';
import { GET_PARTNER_BY_ID } from 'Admin/Partners/graphql';
import { CreateUserDetails, UserDetails } from 'Admin/Users/screens';

export const getRefetchPartnerQuery = (
  id: string | undefined,
  grants: GrantsType
) => ({
  query: GET_PARTNER_BY_ID,
  variables: {
    id,
    canViewPartnerChannelCommission: createPartnerPolicy(grants),
  },
});

export const Router: FC<RouteComponentProps> = ({ match, location }) => {
  const { user } = useAuth0();
  const isPartnerLocationMatch = matchPath<{ id: string }>(location.pathname, {
    path: urlJoin(match.url, '/partners/:id'),
  });

  const partnerId = isPartnerLocationMatch && isPartnerLocationMatch.params.id;

  const { error, loading, data, refetch } = useQuery<{
    partner: PartnerType;
  }>(GET_PARTNER_BY_ID, {
    errorPolicy: 'ignore',
    variables: {
      id: partnerId,
      canViewPartnerChannelCommission: createPartnerPolicy(
        user!.effectiveGrants
      ),
    },
    skip: isMissing(partnerId),
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (partnerId && user!.effectiveGrants) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (error) {
    // handle network errors
    if (error?.networkError) {
      return <NetworkError />;
    } else {
      // handle other errors with message
      return <div>{error.message || 'Partner query error'}</div>;
    }
  }

  const defaultUrl =
    isPresent(user!.partner) && isMissing(partnerId)
      ? `/partners/${user!.partner!.id}`
      : '/all-partners';

  const currentPartner = data && data.partner;

  return (
    <PartnerContext.Provider
      value={{
        currentPartner,
        loading,
        error,
      }}
    >
      <SecondaryMenu>
        <AnimatePresence>
          {isPartnerLocationMatch && currentPartner && (
            <PartnerMenu baseUrl={match.url} partner={currentPartner} />
          )}
        </AnimatePresence>
        <SecondaryMenuLink
          key="All Agencies"
          item={{
            testId: 'all-partners-link',
            name: 'All Agencies',
            path: urlJoin(match.url, '/all-partners'),
          }}
        />
        <SecondaryMenuLink
          key="All Users"
          item={{
            testId: 'all-users-link',
            name: 'All Users',
            path: urlJoin(match.url, '/all-users'),
          }}
        />
      </SecondaryMenu>
      <div className={styles.routeContainer}>
        <AnimatePresence mode="wait">
          <Switch>
            <Route
              path={urlJoin(match.url, '/all-users')}
              render={(props) => <UsersHome {...props} />}
            />
            <Route
              path={urlJoin(match.url, '/all-partners')}
              render={(props) => <PartnersHome {...props} />}
            />
            {/* '/users/create' route needs to be before '/users/:userId' route */}
            <Route
              path={urlJoin(match.url, '/users/create')}
              component={CreateUserDetails}
              exact={true}
            />
            <Route
              path={urlJoin(match.url, '/users/:userId')}
              component={UserDetails}
            />
            {/* '/partners/create' route needs to be before '/partners/:id' route */}
            <Route
              path={urlJoin(match.url, '/partners/create')}
              component={CreatePartnerDetails}
              exact={true}
            />
            <Route
              path={urlJoin(match.url, '/partners/:id')}
              component={PartnerRouter}
            />

            <AnimatedRedirect
              to={urlJoin(match.url, defaultUrl)}
              from={match.url}
            />
          </Switch>
        </AnimatePresence>
      </div>
    </PartnerContext.Provider>
  );
};
